import { useState } from 'react';
import { Button2, Text, Tooltip } from '@frameio/vapor';
import { IconEdit } from '@frameio/vapor-icons';
import { ExportLocationDialog } from '../../ExportLocation';
import TEXT from './generated/TEXT/UploadLocationRow';
import { SettingRow } from './SettingRow';

TEXT.DEFINE({
  /** Label for the upload location selection button */
  label: 'Upload Location',
  /** Label for the upload location selection button when no location is selected */
  selectLocation: 'Select a Frame.io Location',
});

type UploadLocationRowProps = {
  /** Disables the button and greys out the label (e.g. when version-stack mode locks the location). */
  disabled?: boolean;
  /** Tooltip shown when the row is disabled (e.g. explaining why the location is locked). */
  disabledTooltip?: string;
  /** Currently selected folder ID, passed to the location picker dialog. */
  folderId: string | null;
  /** Display name of the selected folder. Falls back to a "Select a Frame.io Location" placeholder. */
  name?: string;
  /** Called when the user confirms a folder selection in the picker dialog. */
  onFolderIdChange: (folderId: string | null) => void;
  /** Optional callback fired when the picker dialog opens (e.g. for analytics tracking). */
  onOpen?: () => void;
  /** Called when the user selects a project in the picker dialog. */
  onProjectIdChange: (projectId: string | null) => void;
  /** Currently selected project ID, passed to the location picker dialog. */
  projectId: string | null;
  /** When true, the picker dialog opens directly to the folder tree instead of the project list. */
  startInFoldersView?: boolean;
};

export const UploadLocationRow = ({
  disabled,
  disabledTooltip,
  folderId,
  name,
  onFolderIdChange,
  onOpen,
  onProjectIdChange,
  projectId,
  startInFoldersView,
}: UploadLocationRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const row = (
    <SettingRow
      field={
        <Button2
          block={true}
          data-testid="upload-location-select"
          iconAfter={<IconEdit />}
          isDisabled={disabled}
          onPress={() => {
            setIsOpen(true);
            onOpen?.();
          }}
          spaceBetween={true}
        >
          <Text truncate={{ type: 'middle', padding: 5 }}>
            {name || TEXT.selectLocation}
          </Text>
        </Button2>
      }
      title={
        <Text color={disabled ? 'disabled' : 'primary'} variant="body">
          {TEXT.label}
        </Text>
      }
    />
  );

  return (
    <>
      {disabled && disabledTooltip ? (
        <Tooltip placement="top" title={disabledTooltip}>
          <div>{row}</div>
        </Tooltip>
      ) : (
        row
      )}
      <ExportLocationDialog
        folderId={folderId}
        isShowing={isOpen}
        onFolderIdChange={onFolderIdChange}
        onProjectIdChange={onProjectIdChange}
        onRequestClose={() => setIsOpen(false)}
        projectId={projectId}
        startInFoldersView={startInFoldersView}
      />
    </>
  );
};
